import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useMember } from './UserContext';
import { supabase } from '../services/supabaseClient';

interface Role {
  id: number;
  name: string;
}

interface PermissionsContextType {
  permissions: string[];
  roles: Role[];
  isAdmin: boolean;
  loading: boolean;
  refetchPermissions: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

export const PermissionsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { member } = useMember();
  const [permissions, setPermissions] = useState<string[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchPermissions = async () => {
    if (!member?.member_id) {
      setPermissions([]);
      setRoles([]);
      setIsAdmin(false);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      // Fetch individual permissions, roles, and admin status in parallel
      const [permissionsResult, rolesResult, adminResult] = await Promise.all([
        // Fetch direct member permissions
        supabase
          .from('member_permissions')
          .select('permission')
          .eq('member_id', member.member_id),

        // Fetch member's roles
        supabase
          .from('member_roles')
          .select('role_id, roles!member_roles_role_id_fkey(id, name)')
          .eq('member_id', member.member_id),

        // Check admin status
        supabase
          .from('chapter_admins')
          .select('member_id')
          .eq('member_id', member.member_id)
          .eq('chapter_id', member.chapters?.chapter_id)
          .single()
      ]);

      // Collect direct permissions
      const directPermissions = permissionsResult.data?.map((perm) => perm.permission) || [];

      // Collect role list
      const memberRoles: Role[] = rolesResult.data?.map((mr: any) => ({
        id: mr.roles.id,
        name: mr.roles.name,
      })) || [];

      // Fetch role permissions for those roles
      const roleIds = rolesResult.data?.map((mr: any) => mr.role_id) || [];
      let rolePermissions: string[] = [];

      if (roleIds.length > 0) {
        const { data: rolePermsData } = await supabase
          .from('role_permissions')
          .select('permission')
          .in('role_id', roleIds);

        rolePermissions = rolePermsData?.map((rp) => rp.permission) || [];
      }

      // Combine direct permissions and role permissions (deduplicate)
      const allPermissions = Array.from(new Set([...directPermissions, ...rolePermissions]));

      setPermissions(allPermissions);
      setRoles(memberRoles);

      // Set admin status (error code PGRST116 means no rows returned, which is fine)
      setIsAdmin(!!adminResult.data && !adminResult.error);

    } catch (err) {
      console.error('Error during permissions fetch:', err);
      setPermissions([]);
      setRoles([]);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, [member?.member_id, member?.chapters?.chapter_id]);

  const refetchPermissions = async () => {
    await fetchPermissions();
  };

  return (
    <PermissionsContext.Provider value={{ permissions, roles, isAdmin, loading, refetchPermissions }}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

/**
 * Hook to check if user has specific permissions
 * @param permissionsToCheck - Array of permissions to check
 * @returns boolean indicating if user has all specified permissions
 */
export const useHasPermission = (permissionsToCheck: string[]): boolean => {
  const { permissions, isAdmin, loading } = usePermissions();

  if (loading) {
    return false;
  }

  // Admins have all permissions
  if (isAdmin) {
    return true;
  }

  // Check if user has all required permissions
  return permissionsToCheck.every((permission) => permissions.includes(permission));
};

/**
 * Hook to check if user has any of the specified permissions (OR logic)
 * @param permissionsToCheck - Array of permissions to check
 * @returns boolean indicating if user has at least one of the specified permissions
 */
export const useHasAnyPermission = (permissionsToCheck: string[]): boolean => {
  const { permissions, isAdmin, loading } = usePermissions();

  if (loading) {
    return false;
  }

  // Admins have all permissions
  if (isAdmin) {
    return true;
  }

  // Check if user has at least one of the required permissions (OR logic)
  return permissionsToCheck.some((permission) => permissions.includes(permission));
};